import { useEffect, useState } from "react";
import { supabase } from "../lib/SupabaseClient";

interface Bank {
    id: number;
    name: string;
    description?: string;
    created_utc?: string;
}

export default function BanksPage() {
    const [banks, setBanks] = useState<Bank[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [newName, setNewName] = useState("");
    const [newDescription, setNewDescription] = useState("");
    const [editingBank, setEditingBank] = useState<Bank | null>(null);

    // Load banks
    useEffect(() => {
        async function loadBanks() {
            try {
                setLoading(true);
                const { data, error } = await supabase
                    .from("question_banks")
                    .select("*")
                    .order("id");

                if (error) throw error;
                setBanks(data || []);
            } catch (err: any) {
                console.error("Error loading banks:", err.message);
                setError("❌ Failed to load banks");
            } finally {
                setLoading(false);
            }
        }

        loadBanks();
    }, []);

    // Create new bank
    async function createBank() {
        try {
            const { data, error } = await supabase
                .from("question_banks")
                .insert([
                    {
                        name: newName,
                        description: newDescription,
                        created_utc: new Date().toISOString(),
                    },
                ])
                .select()
                .single();

            if (error) throw error;
            setBanks([...banks, data]);
            setNewName("");
            setNewDescription("");
        } catch (err: any) {
            console.error("Error creating bank:", err.message);
            setError("❌ Failed to create bank");
        }
    }

    // Update bank
    async function updateBank(bank: Bank) {
        try {
            const { data, error } = await supabase
                .from("question_banks")
                .update({
                    name: bank.name,
                    description: bank.description,
                })
                .eq("id", bank.id)
                .select()
                .single();

            if (error) throw error;

            setBanks(banks.map((b) => (b.id === bank.id ? data : b)));
            setEditingBank(null);
        } catch (err: any) {
            console.error("Error updating bank:", err.message);
            setError("❌ Failed to update bank");
        }
    }

    // Delete bank
    async function deleteBank(id: number) {
        try {
            const { error } = await supabase
                .from("question_banks")
                .delete()
                .eq("id", id);

            if (error) throw error;
            setBanks(banks.filter((b) => b.id !== id));
        } catch (err: any) {
            console.error("Error deleting bank:", err.message);
            setError("❌ Failed to delete bank");
        }
    }

    if (loading) return <p className="p-6">Loading banks...</p>;
    if (error) return <p className="p-6 text-red-600">{error}</p>;

    return (
        <div className="bg-white p-4 rounded shadow">
            <h2 className="text-lg font-bold mb-4">Question Banks</h2>

            {/* Create form */}
            <div className="mb-6">
                <h3 className="font-medium mb-2">Create New Bank</h3>
                <input
                    type="text"
                    placeholder="Bank name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="border rounded p-2 mr-2"
                />
                <input
                    type="text"
                    placeholder="Description"
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    className="border rounded p-2 mr-2"
                />
                <button
                    onClick={createBank}
                    className="px-4 py-2 bg-green-600 text-white rounded"
                >
                    ➕ Add Bank
                </button>
            </div>

            {/* Banks table */}
            {banks.length === 0 ? (
                <p className="text-gray-600">No banks found.</p>
            ) : (
                <table className="w-full border text-sm">
                    <thead>
                        <tr className="bg-gray-50 text-left">
                            <th className="p-2 border">ID</th>
                            <th className="p-2 border">Name</th>
                            <th className="p-2 border">Description</th>
                            <th className="p-2 border">Created</th>
                            <th className="p-2 border">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {banks.map((bank) => (
                            <tr key={bank.id}>
                                <td className="p-2 border">{bank.id}</td>
                                <td className="p-2 border">
                                    {editingBank?.id === bank.id ? (
                                        <input
                                            type="text"
                                            value={editingBank.name}
                                            onChange={(e) =>
                                                setEditingBank({ ...editingBank, name: e.target.value })
                                            }
                                            className="border rounded p-1"
                                        />
                                    ) : (
                                        bank.name
                                    )}
                                </td>
                                <td className="p-2 border">
                                    {editingBank?.id === bank.id ? (
                                        <input
                                            type="text"
                                            value={editingBank.description || ""}
                                            onChange={(e) =>
                                                setEditingBank({
                                                    ...editingBank,
                                                    description: e.target.value,
                                                })
                                            }
                                            className="border rounded p-1"
                                        />
                                    ) : (
                                        bank.description || "-"
                                    )}
                                </td>
                                <td className="p-2 border">
                                    {bank.created_utc
                                        ? new Date(bank.created_utc).toLocaleDateString()
                                        : "-"}
                                </td>
                                <td className="p-2 border">
                                    {editingBank?.id === bank.id ? (
                                        <>
                                            <button
                                                onClick={() => updateBank(editingBank)}
                                                className="px-2 py-1 bg-blue-500 text-white rounded mr-2"
                                            >
                                                Save
                                            </button>
                                            <button
                                                onClick={() => setEditingBank(null)}
                                                className="px-2 py-1 bg-gray-500 text-white rounded"
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                onClick={() => setEditingBank(bank)}
                                                className="px-2 py-1 bg-yellow-500 text-white rounded mr-2"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => deleteBank(bank.id)}
                                                className="px-2 py-1 bg-red-600 text-white rounded"
                                            >
                                                Delete
                                            </button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

